/**
 * The call log's left rail: four sections that fold.
 *
 * It used to be one column of everything - eight saved views, the do-not-call
 * register, the labels, whether the phone rings here, the forwarding switch and
 * the account's outgoing lines, in that order, all of it always drawn. That is
 * roughly twenty rows on a 212px column, and the cost was not tidiness: the
 * bottom half was scrolled past every time somebody wanted a folder, and the
 * two rows an operator actually acts on in a hurry - where is my telephone
 * pointed, is it ringing HERE - were the two furthest down.
 *
 * FOUR GROUPS, EACH ANSWERING ONE QUESTION.
 *   Calls    which slice of the log am I looking at (the do-not-call register
 *            included: it is the one screen here that is not a list of calls,
 *            but it is still "which list is on screen")
 *   Labels   what am I filtered on
 *   Settings where does this telephone send, ring and sound
 *   Lines    which of our numbers do calls go out on
 *
 * Any number of them may be open at once - see `useRailFolds` for why that is
 * not the usual one-open-slot accordion - and each one shut still says its own
 * answer in a word on the heading. That last part is the whole design: a
 * section that hides the folder you are in, or the fact that every incoming
 * call is going to somebody's mobile, has made the rail shorter by making the
 * operator wrong.
 */
import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Ban, Coins, ListChecks, Phone, PhoneForwarded, PhoneOff } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { labelColor, type CrmLabel } from "../../../../lib/crmLabels";
import { formatPhone } from "../../../../lib/phone";
import type { VoiceReachability } from "../../../telephony/device/useVoiceDevice";
import { useSuppressedNumbers } from "../../../telephony/useSuppressedNumbers";
import { Btn, fmtNum } from "../../../ui";
import { CallAvailabilitySwitch } from "../CallAvailabilitySwitch";
import { CallDivert } from "../CallDivert";
import { useCallAvailability } from "../useCallAvailability";
import { useCallDivert } from "../useCallDivert";
import { BLOCKLIST_PATH, COST_PATH, COVERAGE_PATH, DO_NOT_CALL_PATH, type CallReport } from "../reports";
import { FOLDER_ICON, FOLDER_ORDER, type CallFolder } from "./folders";
import { RailAlert } from "./RailAlert";
import { RailAudio } from "./RailAudio";
import { RailFold } from "./RailFold";
import { useCallerLines } from "./useCallerLines";
import { useRailFolds } from "./useRailFolds";

/** The dot every "state of the telephone" row in this rail is marked with. */
function reachColor(reach: VoiceReachability): string {
  return reach === "ready"
    ? "var(--color-success)"
    : reach === "failed"
      ? "var(--color-destructive)"
      : reach === "elsewhere"
        ? "var(--color-warning)"
        : "var(--color-muted-foreground)";
}

export const CallRail = forwardRef<
  HTMLDivElement,
  {
    folder: CallFolder;
    onFolder: (f: CallFolder) => void;
    labels: CrmLabel[];
    tag: string | null;
    onTag: (id: string | null) => void;
    onDial: () => void;
    canCall: boolean;
    /** True while the do-not-call screen is the one showing. It is not a folder -
     *  it queries a different table - so it lights up on its own. */
    /** The coverage report is the open screen, so no folder is. */
    /** Which non-log screen is open, or null on the call log. */
    report: CallReport | null;
    /** Would a call arriving now ring in THIS window? See `VoiceReachability`. */
    reach: VoiceReachability;
    /** Only read on a phone, where this rail is a sheet over the panes. */
    open: boolean;
    /** A view or a label was chosen. On a phone that closes the sheet: the answer
     *  to "show me the missed ones" is the list, not the rail. */
    onPicked: () => void;
    /**
     * How many calls are still owed a call back, desk-wide.
     *
     * The only folder here that carries a number, and deliberately: the rest are
     * views over a log that never empties, so a count beside them would be an
     * odometer. This one is work, and a work list that does not say how much is
     * left is a folder nobody opens.
     */
    callbacks: number;
  }
>(function CallRail(
  { folder, onFolder, labels, tag, onTag, onDial, canCall, report, reach, open, onPicked, callbacks },
  // The workspace's rail divider measures this column to know where to sit and
  // how far it may be dragged.
  ref,
) {
  const t = useT();
  const suppressed = useSuppressedNumbers();
  const lines = useCallerLines();
  // Held here rather than inside the switch, so a closed Settings heading can
  // still say that calls are being forwarded. See `useCallDivert`.
  const divert = useCallDivert(canCall);
  const availability = useCallAvailability(canCall);
  const folds = useRailFolds();

  const activeLabel = labels.find((l) => l.id === tag) ?? null;
  const forwarding = divert.state?.active ?? false;
  // The switch being off makes "this window rings" untrue, so while it is off the
  // rows that say where the phone would ring step aside.
  const silenced = availability.state?.inboundReady === true && !availability.state.online;
  const line = lines.rows.find((r) => r.phoneNumber === lines.selected) ?? lines.rows[0];

  return (
    <div ref={ref} className={cn("crm-card crm-work-col crm-pane-side", open && "is-open")}>
      {canCall ? (
        // Brand, not dark - same reason as the mail rail's Compose: the selected
        // view below it is a black pill, and the action must not look like one.
        // The plate is what stays put while the views scroll (`.crm-pane-act`).
        <div className="crm-pane-act">
          <Btn tone="brand" onClick={onDial}>
            <Phone className="size-4" />
            {t("calls.dial.title")}
          </Btn>
        </div>
      ) : null}

      <RailFold
        title={t("calls.pane.calls")}
        open={folds.open.has("calls")}
        onToggle={() => folds.toggle("calls")}
        summary={
          <span className="crm-acc-s">
            {report === "do-not-call"
              ? t("calls.suppression.title")
              : report === "blocklist"
                ? t("calls.blocklist.title")
                : report === "coverage"
                  ? t("calls.coverage.title")
                  : report === "cost"
                    ? t("calls.cost.title")
                    : t(`calls.pane.${folder}`)}
          </span>
        }
      >
        {FOLDER_ORDER.map((key) => {
          const Icon = FOLDER_ICON[key];
          return (
            <button
              key={key}
              type="button"
              className={cn("crm-fold", !report && folder === key && "is-on")}
              // Single-select, like the mail rail: this is where you are, not one
              // of several switches that happen to be pressed.
              aria-current={!report && folder === key ? "true" : undefined}
              onClick={() => {
                onFolder(key);
                onPicked();
              }}
            >
              <Icon className="size-4" strokeWidth={1.8} />
              <span className="crm-fold-l">{t(`calls.pane.${key}`)}</span>
              {key === "callbacks" && callbacks > 0 ? (
                <span className="crm-n">{fmtNum(callbacks)}</span>
              ) : null}
            </button>
          );
        })}

        {/* Under a rule rather than beside the eight above it: every entry up
            there is a query over the call log, and this one is the register of
            people who asked us to stop - a different table, and the one screen
            in this section that is not a list of calls. A real link rather than
            a button, because it IS a route: the browser's back button then walks
            between the log and the list for free. */}
        <span className="crm-acc-sep" />
        <Link
          to={DO_NOT_CALL_PATH}
          className={cn("crm-fold", report === "do-not-call" && "is-on")}
          aria-current={report === "do-not-call" ? "page" : undefined}
          onClick={onPicked}
        >
          <PhoneOff className="size-4" strokeWidth={1.8} />
          <span className="crm-fold-l">{t("calls.suppression.title")}</span>
          {/* Only once there is something to count. A zero here would read as a
              broken counter rather than as an empty list, which is the one thing
              the screen behind it says perfectly well itself. */}
          {suppressed && suppressed.count > 0 ? <span className="crm-n">{fmtNum(suppressed.count)}</span> : null}
        </Link>
        {/* Directly under the do-not-call list because they are the pair, and
            the pair is the point: one is people who asked US to stop, the other
            is callers WE refuse. Adjacent so nobody goes looking for the second
            one inside the first.

            No counter, and that is not an oversight. The do-not-call count is
            already in the shell - every operator loads that list at sign-in to
            strike numbers through - so it costs nothing to print. This list is
            read by nothing else, and fetching it on every page load to put a
            number in the rail would be a request per session for a figure
            nobody is waiting on. */}
        <Link
          to={BLOCKLIST_PATH}
          className={cn("crm-fold", report === "blocklist" && "is-on")}
          aria-current={report === "blocklist" ? "page" : undefined}
          onClick={onPicked}
        >
          <Ban className="size-4" strokeWidth={1.8} />
          <span className="crm-fold-l">{t("calls.blocklist.title")}</span>
        </Link>
        {/* Beside it rather than above: this is the other screen here that is
            not a list of calls. No counter - the number it would carry is a
            percentage of a period nobody has chosen yet, and a figure in the
            rail that changes meaning with a control on the screen behind it is
            worse than none. */}
        <Link
          to={COVERAGE_PATH}
          className={cn("crm-fold", report === "coverage" && "is-on")}
          aria-current={report === "coverage" ? "page" : undefined}
          onClick={onPicked}
        >
          <ListChecks className="size-4" strokeWidth={1.8} />
          <span className="crm-fold-l">{t("calls.coverage.title")}</span>
        </Link>
        {/* The third screen here that is not a list of calls, and no counter for
            the same reason the one above has none: the figure it would carry is
            money over a period nobody has chosen yet, and a number in the rail
            that changes meaning with a control on the screen behind it is worse
            than no number. */}
        <Link
          to={COST_PATH}
          className={cn("crm-fold", report === "cost" && "is-on")}
          aria-current={report === "cost" ? "page" : undefined}
          onClick={onPicked}
        >
          <Coins className="size-4" strokeWidth={1.8} />
          <span className="crm-fold-l">{t("calls.cost.title")}</span>
        </Link>
      </RailFold>

      {labels.length > 0 ? (
        <RailFold
          title={t("calls.pane.labels")}
          open={folds.open.has("labels")}
          onToggle={() => folds.toggle("labels")}
          // A filter is on and its section is shut: the list below is short for
          // a reason nothing else on the screen would give.
          summary={
            activeLabel ? (
              <span className="crm-acc-s">
                <i style={{ background: labelColor(activeLabel.tone) }} />
                {activeLabel.name}
              </span>
            ) : null
          }
        >
          {labels.map((label) => (
            <button
              key={label.id}
              type="button"
              className={cn("crm-mtag", tag === label.id && "is-on")}
              aria-pressed={tag === label.id}
              onClick={() => {
                onTag(tag === label.id ? null : label.id);
                onPicked();
              }}
            >
              <i style={{ background: labelColor(label.tone) }} />
              <span className="crm-mtag-l">{label.name}</span>
            </button>
          ))}
        </RailFold>
      ) : null}

      <RailFold
        title={t("calls.pane.settings")}
        open={folds.open.has("settings")}
        onToggle={() => folds.toggle("settings")}
        // Two marks, and both of them are about calls that are NOT arriving
        // here. Forwarding is drawn first because it outranks the rest: a
        // telephone pointed at somebody's mobile explains an empty log, and an
        // operator who cannot see that from a folded heading spends the morning
        // wondering why nobody is ringing.
        summary={
          <span className="crm-acc-s">
            {forwarding ? <PhoneForwarded className="size-3.5" strokeWidth={2} /> : null}
            {silenced ? (
              <PhoneOff className="size-3.5" strokeWidth={2} />
            ) : reach !== "off" ? (
              <i style={{ background: reachColor(reach) }} />
            ) : null}
          </span>
        }
      >
        {canCall ? <CallAvailabilitySwitch availability={availability} /> : null}

        {/* Draws nothing on an installation that does not answer its number, and
            nothing for an operator who may not place calls - the switch writes
            the same settings row the dial button's permission guards. */}
        {canCall ? <CallDivert divert={divert} /> : null}

        {/*
          Whether the phone would ring HERE.
          Nothing said this before, and the cost of the silence was a day of
          testing: with three CRM windows open, Twilio rang whichever one it
          picked, and from the operator's chair that looked like "calls from that
          number never arrive". `off` draws nothing - an installation that does
          not answer its phone has nothing to report about where it would ring.
        */}
        {reach !== "off" && !silenced ? (
          <>
            <div className="crm-msect">{t("calls.reach.title")}</div>
            <span
              className="crm-mtag"
              title={
                reach === "elsewhere"
                  ? t("calls.reach.elsewhereHint")
                  : reach === "failed"
                    ? t("calls.reach.failedHint")
                    : undefined
              }
            >
              <i style={{ background: reachColor(reach) }} />
              <span className="crm-mtag-l">{t(`calls.reach.${reach}`)}</span>
            </span>
          </>
        ) : null}

        {/* The last two are the only ones about this browser rather than about
            the account: where the call goes in and where it comes out, and
            whether a ringing phone may reach past the tab at all. Both are
            granted and stored per browser profile, so both belong on the screen
            the operator is sitting in front of rather than in account settings. */}
        <RailAlert />
        <RailAudio />
      </RailFold>

      {lines.rows.length > 0 ? (
        <RailFold
          title={t("calls.pane.lines")}
          open={folds.open.has("lines")}
          onToggle={() => folds.toggle("lines")}
          summary={line ? <span className="crm-acc-s tabular-nums">{formatPhone(line.phoneNumber)}</span> : null}
        >
          {/* Keyed by the number: the list merges two Twilio resources and the
              number is what they were de-duplicated on. */}
          {lines.rows.map((row, i) => (
            <span key={row.phoneNumber} className="crm-mtag">
              <i
                style={{
                  background:
                    row.phoneNumber === lines.selected ? "var(--color-success)" : "var(--color-muted-foreground)",
                }}
              />
              <span className="crm-mtag-l" title={row.friendlyName}>
                {formatPhone(row.phoneNumber)}
              </span>
              {i === 0 && row.phoneNumber === lines.selected ? <span className="crm-n">{t("common.yes")}</span> : null}
            </span>
          ))}
        </RailFold>
      ) : null}
    </div>
  );
});
